<template>
  <label for="tiptap-editor" data-testid="so-texteditor-label">{{
    label
  }}</label>
  <v-card
    v-if="editor"
    class="so-editor border rounded"
    :class="{ 'so-editor--dragging': isDragging }"
    variant="flat"
    v-bind="$attrs"
    @dragenter.prevent="handleDragEnter"
    @dragover.prevent="handleDragOver"
    @dragleave.prevent="handleDragLeave"
    @drop.prevent="handleDrop"
  >
    <!-- Toolbar -->
    <v-sheet
      color="surface-dim"
      class="so-editor__toolbar d-flex gap-1 py-1 px-2"
    >
      <div
        v-if="features.includes('formatting') || features.includes('highlight')"
        class="editor-group d-flex gap-1"
      >
        <v-btn
          flat
          density="compact"
          variant="text"
          icon="bold sm"
          data-testid="so-texteditor-bold-btn"
          :disabled="!editor.can().chain().focus().toggleMark('bold').run()"
          :class="{ 'is-active': editor.isActive('bold') }"
          @click="editor.chain().focus().toggleMark('bold').run()"
        />
        <v-btn
          flat
          density="compact"
          variant="text"
          icon="italic sm"
          data-testid="so-texteditor-italic-btn"
          :disabled="!editor.can().chain().focus().toggleMark('italic').run()"
          :class="{ 'is-active': editor.isActive('italic') }"
          @click="editor.chain().focus().toggleMark('italic').run()"
        />
        <v-btn
          flat
          density="compact"
          variant="text"
          icon="underline sm"
          data-testid="so-texteditor-underline-btn"
          :disabled="!editor.can().chain().focus().toggleUnderline().run()"
          :class="{ 'is-active': editor.isActive('underline') }"
          @click="editor.chain().focus().toggleUnderline().run()"
        />
        <v-btn
          v-if="features.includes('highlight')"
          flat
          density="compact"
          variant="text"
          icon="highlight sm"
          data-testid="so-texteditor-highlight-btn"
          :disabled="!editor.can().chain().focus().toggleHighlight().run()"
          :class="{ 'is-active': editor.isActive('highlight') }"
          @click="editor.chain().focus().toggleHighlight().run()"
        />
      </div>

      <div v-if="features.includes('link')" class="editor-group d-flex gap-1">
        <v-btn
          flat
          density="compact"
          variant="text"
          icon="link sm"
          data-testid="so-texteditor-link-btn"
          :disabled="editor.state.selection.empty"
          :class="{ 'is-active': editor.isActive('link') }"
          @click="openLinkDialog"
        />
      </div>

      <div v-if="features.includes('list')" class="editor-group d-flex gap-1">
        <v-btn
          flat
          density="compact"
          variant="text"
          icon="list-ordered sm"
          data-testid="so-texteditor-ol-btn"
          :class="{ 'is-active': editor.isActive('orderedList') }"
          @click="editor.chain().focus().toggleOrderedList().run()"
        />
        <v-btn
          flat
          density="compact"
          variant="text"
          icon="list sm"
          data-testid="so-texteditor-ul-btn"
          :class="{ 'is-active': editor.isActive('bulletList') }"
          @click="editor.chain().focus().toggleBulletList().run()"
        />
      </div>

      <div v-if="features.includes('align')" class="editor-group d-flex gap-1">
        <v-btn
          flat
          density="compact"
          variant="text"
          icon="align-left sm"
          data-testid="so-texteditor-left-align-btn"
          @click="editor.chain().focus().setTextAlign('left').run()"
        />
        <v-btn
          flat
          density="compact"
          variant="text"
          icon="align-center sm"
          data-testid="so-texteditor-center-align-btn"
          @click="editor.chain().focus().setTextAlign('center').run()"
        />
        <v-btn
          flat
          density="compact"
          variant="text"
          icon="align-right sm"
          data-testid="so-texteditor-right-align-btn"
          @click="editor.chain().focus().setTextAlign('right').run()"
        />
      </div>

      <div
        v-if="features.includes('blockquote')"
        class="editor-group d-flex gap-1"
      >
        <v-btn
          flat
          density="compact"
          variant="text"
          icon="text-quote sm"
          data-testid="so-texteditor-blockquote-btn"
          :class="{ 'is-active': editor.isActive('blockquote') }"
          @click="editor.chain().focus().toggleBlockquote().run()"
        />
      </div>

      <div v-if="features.includes('code')" class="editor-group d-flex gap-1">
        <v-btn
          density="compact"
          variant="text"
          flat
          icon="code sm"
          data-testid="so-texteditor-code-btn"
          :disabled="!editor.can().chain().focus().toggleMark('code').run()"
          :class="{ 'is-active': editor.isActive('code') }"
          @click="editor.chain().focus().toggleMark('code').run()"
        />
      </div>

      <!-- Image Upload Button -->
      <div v-if="features.includes('image')" class="editor-group d-flex gap-1">
        <v-btn
          flat
          density="compact"
          variant="text"
          icon="file-image sm"
          data-testid="so-texteditor-image-btn"
          title="Insert image (or drag & drop, paste)"
          @click="triggerImageUpload"
        />
        <input
          ref="imageInput"
          type="file"
          accept="image/*"
          style="display: none"
          @change="handleImageSelect"
        />
      </div>

      <v-spacer />
      <v-btn
        flat
        density="compact"
        variant="text"
        icon="undo sm"
        :disabled="false"
        @click="editor?.commands.undo()"
      />
      <v-btn
        flat
        density="compact"
        variant="text"
        icon="redo sm"
        :disabled="false"
        @click="editor?.commands.redo()"
      />
    </v-sheet>

    <!-- Editor Content -->
    <div class="position-relative">
      <editor-content
        id="tiptap-editor"
        ref="editor"
        data-testid="so-texteditor-content"
        :editor="editor"
      />

      <!-- Drag & Drop Overlay -->
      <Transition name="fade">
        <div
          v-if="isDragging && features.includes('image')"
          class="so-editor__overlay d-flex align-center justify-center"
        >
          <div
            class="so-editor__drop-zone d-flex flex-column align-center pa-10 rounded-xl"
          >
            <div class="so-editor__drop-icon mb-4">
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </div>
            <p class="text-h6 font-weight-semibold mb-1">Drop image here</p>
            <p class="text-caption text-medium-emphasis">
              PNG, JPG, GIF up to 10MB
            </p>
          </div>
        </div>
      </Transition>

      <!-- Upload Progress Overlay -->
      <Transition name="fade">
        <div
          v-if="isUploading"
          class="so-editor__overlay d-flex align-center justify-center"
        >
          <div class="d-flex flex-column align-center ga-4">
            <v-progress-circular
              indeterminate
              color="primary"
              size="40"
              width="3"
            />
            <p class="text-body-2 font-weight-medium">Uploading image...</p>
          </div>
        </div>
      </Transition>
    </div>

    <!-- Image hint bar -->
    <SoDivider v-if="features.includes('image')" />
    <div
      v-if="features.includes('image')"
      class="d-flex align-center pa-3 text-caption bg-surface-variant rounded-b"
    >
      <v-icon size="14" icon="lightbulb" class="mr-1" />
      <span
        >Tip: Paste images from clipboard (Ctrl+V) or drag & drop directly into
        the editor</span
      >
    </div>
  </v-card>

  <!-- Link Dialog -->
  <SoDialog v-model="isLinkDialogOpen" card-title="Add Link">
    <SoTextField v-model="url" label="Link" />
    <div class="d-flex gap-1 justify-end mt-8">
      <SoButton title="Cancel" large @click="isLinkDialogOpen = false" />
      <SoButton title="Save" type="primary" large @click="setLink" />
    </div>
  </SoDialog>
</template>

<script lang="ts">
  import { ShallowRef, PropType } from "vue"
  import { Editor, EditorContent } from "@tiptap/vue-3"
  import { useThemeHelper } from "@/theme/themeHelper"
  import { useSnackbarStore } from "@/stores/snackbar"
  import TiptapDocument from "@tiptap/extension-document"
  import Paragraph from "@tiptap/extension-paragraph"
  import TiptapText from "@tiptap/extension-text"
  import Bold from "@tiptap/extension-bold"
  import Italic from "@tiptap/extension-italic"
  import Code from "@tiptap/extension-code"
  import Blockquote from "@tiptap/extension-blockquote"
  import OrderedList from "@tiptap/extension-ordered-list"
  import BulletList from "@tiptap/extension-bullet-list"
  import ListItem from "@tiptap/extension-list-item"
  import TiptapHistory from "@tiptap/extension-history"
  import Underline from "@tiptap/extension-underline"
  import TextAlign from "@tiptap/extension-text-align"
  import Highlight from "@tiptap/extension-highlight"
  import Link from "@tiptap/extension-link"
  import Table from "@tiptap/extension-table"
  import TableCell from "@tiptap/extension-table-cell"
  import TableHeader from "@tiptap/extension-table-header"
  import TableRow from "@tiptap/extension-table-row"
  import ImageResize from "tiptap-extension-resize-image"
  import Dropcursor from "@tiptap/extension-dropcursor"
  import Placeholder from "@tiptap/extension-placeholder"
  import HardBreak from "@tiptap/extension-hard-break"
  import SoDialog from "./SoDialog.vue"
  import SoTextField from "./formControls/SoTextField.vue"
  import SoButton from "./formControls/SoButton.vue"
  import SoDivider from "./SoDivider.vue"

  type Features =
    | "formatting"
    | "highlight"
    | "link"
    | "list"
    | "align"
    | "blockquote"
    | "code"
    | "image"

  export default {
    components: {
      EditorContent,
      SoDialog,
      SoTextField,
      SoButton,
      SoDivider,
    },
    inheritAttrs: false,
    props: {
      modelValue: {
        type: String,
        default: "",
      },
      label: {
        type: String,
        default: null,
      },
      features: {
        type: Array as PropType<Features[]>,
        default: () => [
          "formatting",
          "highlight",
          "link",
          "list",
          "align",
          "blockquote",
          "code",
        ],
      },
      imageUploadHandler: {
        type: Function as PropType<(file: File) => Promise<string>>,
        default: null,
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        editor: null as ShallowRef<Editor> | null,
        isLinkDialogOpen: false,
        url: "",
        isDragging: false,
        isUploading: false,
        dragCounter: 0,
        theme: useThemeHelper(),
        snackbar: useSnackbarStore(),
      }
    },
    watch: {
      modelValue(value) {
        const isSame = this.editor?.getHTML() === value
        if (isSame) return

        this.editor?.commands.setContent(value, false)
      },
    },
    mounted() {
      this.editor = new Editor({
        extensions: [
          TiptapDocument,
          Paragraph,
          TiptapText,
          Bold,
          Italic,
          Code,
          Blockquote.configure({
            HTMLAttributes: {
              class: "blockquote",
            },
          }),
          OrderedList,
          BulletList,
          ListItem,
          TiptapHistory,
          Underline,
          TextAlign.configure({
            types: ["heading", "paragraph"],
            alignments: ["left", "center", "right"],
          }),
          Highlight,
          Link,
          Table,
          TableRow,
          TableHeader,
          TableCell,
          ImageResize.configure({
            inline: false,
            allowBase64: true,
          } as Record<string, unknown>),
          Dropcursor.configure({
            color: this.theme.colors.focus,
            width: 3,
          }),
          Placeholder.configure({
            placeholder: "Start typing or paste/drag an image...",
          }),
          HardBreak as never,
        ],
        content: this.modelValue,
        onUpdate: () => {
          this.$emit("update:modelValue", this.editor?.getHTML())
        },
        editorProps: {
          handlePaste: (view, event) => {
            return this.handleImageEvent(event, "paste")
          },
          handleDrop: (view, event) => {
            return this.handleImageEvent(event, "drop")
          },
        },
      })
    },
    beforeUnmount() {
      this.editor?.destroy()
    },
    methods: {
      handleImageEvent(
        event: ClipboardEvent | DragEvent,
        type: "paste" | "drop",
      ): boolean {
        if (!this.features.includes("image")) return false

        let file: File | null = null

        if (type === "paste") {
          const items = (event as ClipboardEvent).clipboardData?.items
          if (!items) return false

          for (const item of Array.from(items)) {
            if (item.type.startsWith("image/")) {
              file = item.getAsFile()
              break
            }
          }
        } else {
          const files = (event as DragEvent).dataTransfer?.files
          if (!files || files.length === 0) return false

          const firstFile = files[0]
          if (firstFile.type.startsWith("image/")) {
            file = firstFile
          }
        }

        if (file) {
          event.preventDefault()
          this.uploadAndInsertImage(file)
          return true
        }

        return false
      },
      openLinkDialog() {
        this.url = this.editor?.getAttributes("link").href
        this.isLinkDialogOpen = true
      },
      setLink() {
        if (this.url === "") {
          this.editor?.chain().focus().extendMarkRange("link").unsetLink().run()
          this.isLinkDialogOpen = false
          return
        }

        this.editor
          ?.chain()
          .focus()
          .extendMarkRange("link")
          .setLink({ href: this.url })
          .run()

        this.isLinkDialogOpen = false
      },
      triggerImageUpload() {
        ;(this.$refs.imageInput as HTMLInputElement)?.click()
      },
      handleImageSelect(event: Event) {
        const input = event.target as HTMLInputElement
        const file = input.files?.[0]
        if (file) {
          this.uploadAndInsertImage(file)
        }
        input.value = ""
      },
      handleDragEnter(event: DragEvent) {
        if (!this.features.includes("image")) return
        this.dragCounter++
        if (event.dataTransfer?.types.includes("Files")) {
          this.isDragging = true
        }
      },
      handleDragOver(event: DragEvent) {
        if (!this.features.includes("image")) return
        if (event.dataTransfer) {
          event.dataTransfer.dropEffect = "copy"
        }
      },
      handleDragLeave() {
        if (!this.features.includes("image")) return
        this.dragCounter--
        if (this.dragCounter === 0) {
          this.isDragging = false
        }
      },
      handleDrop(event: DragEvent) {
        if (!this.features.includes("image")) return
        this.isDragging = false
        this.dragCounter = 0

        const files = event.dataTransfer?.files
        if (!files || files.length === 0) return

        const file = files[0]
        if (file.type.startsWith("image/")) {
          this.uploadAndInsertImage(file)
        }
      },
      async uploadAndInsertImage(file: File) {
        if (!file.type.startsWith("image/")) {
          this.snackbar.addError("File is not an image")
          return
        }

        // Check file size (10MB limit)
        if (file.size > 10 * 1024 * 1024) {
          this.snackbar.addError("Image file is too large (max 10MB)")
          return
        }

        this.isUploading = true

        try {
          let imageUrl: string

          if (this.imageUploadHandler) {
            // Use custom upload handler (e.g., upload to S3)
            imageUrl = await this.imageUploadHandler(file)
          } else {
            // Fallback: Convert to base64
            imageUrl = await this.fileToBase64(file)
          }

          if (imageUrl) {
            // Insert image into editor
            ;(this.editor?.chain().focus() as any)
              .setImage({ src: imageUrl })
              .run()
          }
        } catch (error) {
          console.error("Failed to upload image:", error)
          this.snackbar.addError("Failed to upload image")
        } finally {
          this.isUploading = false
        }
      },
      fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result as string)
          reader.onerror = reject
          reader.readAsDataURL(file)
        })
      },
    },
  }
</script>

<style>
  .tiptap {
    margin: 2px;
    padding: 16px;
    border-radius: 2px;
    min-height: 200px;
  }
  .tiptap:focus-visible {
    outline: var(--theme-outline);
  }
  .tiptap ol {
    list-style-type: decimal;
    margin-left: 20px;
  }
  .tiptap ul {
    list-style-type: disc;
    margin-left: 20px;
  }
  .tiptap a {
    cursor: pointer;
  }
  .tiptap table,
  .tiptap th,
  .tiptap td {
    border-collapse: collapse;
    border: 1px solid rgb(var(--v-theme-border));
    padding: 0 6px;
  }

  /* Image styles for tiptap-extension-resize-image */
  .tiptap img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 12px 0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    transition: box-shadow 0.2s ease;
    cursor: pointer;
  }

  .tiptap img:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }

  .tiptap img.ProseMirror-selectednode {
    outline: 3px solid rgb(var(--v-theme-focus));
    outline-offset: 2px;
  }

  /* Image resize wrapper styles */
  .tiptap .image-resizer {
    display: inline-flex;
    position: relative;
    flex-grow: 0;
    margin: 12px 0;
  }

  .tiptap .image-resizer img {
    margin: 0;
  }

  .tiptap .image-resizer .resize-trigger {
    position: absolute;
    right: -6px;
    bottom: -6px;
    width: 14px;
    height: 14px;
    background: rgb(var(--v-theme-focus));
    border: 2px solid rgb(var(--v-theme-background));
    border-radius: 50%;
    cursor: se-resize;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    opacity: 0;
    transition: opacity 0.2s;
  }

  .tiptap .image-resizer:hover .resize-trigger,
  .tiptap .image-resizer.resizing .resize-trigger {
    opacity: 1;
  }

  /* Placeholder styles */
  .tiptap p.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    float: left;
    color: rgb(var(--v-theme-disabled));
    pointer-events: none;
    height: 0;
    font-style: italic;
  }
</style>

<style scoped>
  .so-editor--dragging {
    border-color: rgb(var(--v-theme-focus)) !important;
    box-shadow: 0 0 0 3px rgba(255, 209, 0, 0.2);
  }

  .so-editor__overlay {
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(4px);
    z-index: 10;
    border-radius: 0 0 4px 4px;
  }

  .so-editor__drop-zone {
    border: 2px dashed rgb(var(--v-theme-focus));
    background: rgba(255, 209, 0, 0.05);
  }

  .so-editor__drop-icon {
    color: rgb(var(--v-theme-focus));
    animation: bounce 1s ease infinite;
  }

  @keyframes bounce {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-8px);
    }
  }

  .is-active {
    background: rgb(var(--v-theme-border));
  }

  .editor-group {
    border-left: 1px solid rgb(var(--v-theme-border));
  }

  .editor-group:first-of-type {
    border-left: none;
    padding-left: 0;
  }
</style>
